/**
 * Rating Component
 * Ported from afrodita-components - Displays star rating with review count.
 * Supports precise partial-fill stars (e.g. 4.7 renders a 70%-filled 5th star).
 */

import React from 'react';
import styled from '@emotion/styled';
import { useTheme } from '../theme/ThemeProvider';
import { ComponentSize } from '../types';

const STAR_PATH =
  'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';

interface StarIconProps {
  width?: number;
  height?: number;
  fill?: string;
}

const StarIcon = ({ width = 16, height = 16, fill = '#FDB913' }: StarIconProps) => (
  <svg width={width} height={height} viewBox="0 0 24 24" fill={fill} xmlns="http://www.w3.org/2000/svg">
    <path d={STAR_PATH} />
  </svg>
);

let partialStarCounter = 0;

interface PartialStarIconProps {
  width?: number;
  height?: number;
  fill?: string;
  emptyFill?: string;
  fraction: number;
}

const PartialStarIcon = ({
  width = 16,
  height = 16,
  fill = '#FDB913',
  emptyFill = '#d1d5db',
  fraction,
}: PartialStarIconProps) => {
  const gradientId = `rating-partial-star-${partialStarCounter++}`;
  const pct = `${Math.round(fraction * 100)}%`;
  return (
    <svg width={width} height={height} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gradientId}>
          <stop offset={pct} stopColor={fill} />
          <stop offset={pct} stopColor={emptyFill} />
        </linearGradient>
      </defs>
      <path d={STAR_PATH} fill={`url(#${gradientId})`} />
    </svg>
  );
};

export interface RatingProps {
  readonly rating: number;
  readonly size?: ComponentSize;
  readonly reviews?: number;
  readonly reviewsText?: string;
  readonly wrapWithParenthesis?: boolean;
  readonly showRatingText?: boolean;
  readonly underline?: boolean;
  readonly reviewsContainerId?: string;
  readonly additionalContent?: React.ReactNode;
}

const Container = styled.a`
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  cursor: pointer;
  gap: 6px;
`;

const StarsContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 1px;
`;

const RatingLabel = styled.span<{ color: string; $size: ComponentSize }>`
  font-size: ${({ theme, $size }) => {
    switch ($size) {
      case ComponentSize.XSmall:
        return theme.fontSizes[0];
      case ComponentSize.Small:
        return theme.fontSizes[1];
      case ComponentSize.Medium:
        return theme.fontSizes[2];
      case ComponentSize.Large:
        return theme.fontSizes[3];
      default:
        return theme.fontSizes[2];
    }
  }}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.demi};
  color: ${({ color }) => color};
  text-decoration: none;
  line-height: 1;
`;

interface CustomLabelProps {
  $size: ComponentSize;
  $wrapWithParenthesis?: boolean;
  $underline?: boolean;
}

const CustomLabel = styled.span<CustomLabelProps>`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: ${({ theme }) => theme.typography.config.weight.regular};
  font-size: ${({ theme, $size }) => {
    switch ($size) {
      case ComponentSize.XSmall:
        return theme.fontSizes[0];
      case ComponentSize.Small:
        return theme.fontSizes[1];
      case ComponentSize.Medium:
        return theme.fontSizes[2];
      case ComponentSize.Large:
        return theme.fontSizes[3];
      default:
        return theme.fontSizes[2];
    }
  }}px;
  line-height: 1;
  text-decoration: ${({ $underline }) => ($underline ? 'underline' : 'none')};
`;

const STARS_NUMBER = 5;

export function Rating({
  size = ComponentSize.Medium,
  reviews,
  reviewsText,
  rating,
  showRatingText = false,
  wrapWithParenthesis = false,
  underline = false,
  reviewsContainerId,
  additionalContent,
}: RatingProps) {
  const theme = useTheme();

  const handleAnchorClick = (e: React.MouseEvent) => {
    if (e.cancelable) {
      e.preventDefault();
    }

    if (reviewsContainerId) {
      document.querySelector(reviewsContainerId)?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  };

  const containerAttributes = reviewsContainerId
    ? {
        href: reviewsContainerId,
      }
    : {};

  // Size mapping for star icons
  const getStarSize = () => {
    switch (size) {
      case ComponentSize.XSmall:
        return { width: 14, height: 14 };
      case ComponentSize.Small:
        return { width: 16, height: 16 };
      case ComponentSize.Medium:
        return { width: 18, height: 18 };
      case ComponentSize.Large:
        return { width: 20, height: 20 };
      default:
        return { width: 16, height: 16 };
    }
  };

  const starSize = getStarSize();
  const starFill = '#FDB913';
  const emptyFill = theme.colors.border?.color || '#d1d5db';

  const floorRating = Math.floor(rating);
  const fraction = rating - floorRating;

  const reviewLabel = (() => {
    if (!reviews && !reviewsText) return null;
    const countStr = reviews != null ? `${reviews}` : '';
    const textStr = reviewsText ?? '';
    const inner = [countStr, textStr].filter(Boolean).join(' ');
    return wrapWithParenthesis ? `(${inner})` : inner;
  })();

  return (
    <Container
      as={reviewsContainerId ? 'a' : 'div'}
      {...containerAttributes}
      data-testid="rating-container"
      onClick={handleAnchorClick}
    >
      {showRatingText && (
        <RatingLabel color={theme.colors.text.primary} $size={size}>
          {rating.toFixed(1)}
        </RatingLabel>
      )}
      <StarsContainer>
        {[...Array(STARS_NUMBER)].map((_, index) => {
          if (index < floorRating) {
            return <StarIcon key={`star-${index}`} {...starSize} fill={starFill} />;
          }
          if (index === floorRating && fraction > 0) {
            return (
              <PartialStarIcon
                key={`star-${index}`}
                {...starSize}
                fill={starFill}
                emptyFill={emptyFill}
                fraction={fraction}
              />
            );
          }
          return <StarIcon key={`star-${index}`} {...starSize} fill={emptyFill} />;
        })}
      </StarsContainer>
      {reviewLabel && (
        <CustomLabel
          $size={size}
          $underline={underline}
          data-testid="review-count"
        >
          {reviewLabel}
        </CustomLabel>
      )}
      {additionalContent}
    </Container>
  );
}

